﻿import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Typography, Box } from '@material-ui/core';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import newTimeSlotsPackage from '../../../propTypes/entities/newTimeSlotsPackage';
import Spinner from '../../common/Spinner/Spinner';
import TimeSlotConfiguration from './TimeSlotConfiguration';
import globals from '../../../globals/communityGlobals';
import { getNextName } from '../../../utils/timeslots';
import DefaultPageWrapper from '../../../containers/PageStructure/DefaultPageWrapper/DefaultPageWrapperContainer';
import ConfirmationDialog from '../../common/ConfirmationDialog/ConfirmationDialog';

const locale = globals.getAreas(['configureTimeSlots', 'error']);
const defaultDifferenceBetweenStartAndEndOfTimeslotInMinutes = 60;

class ConfigureTimeSlots extends React.Component {
  state = { showSaveTimeSlotsConformation: false };

  componentDidMount() {
    this.props.getDefaultTimeSlotsPackage();
  }

  getTimeSlotByName = timeSlotName => {
    const { defaultTimeSlotPackage } = this.props;
    const timeSlots = defaultTimeSlotPackage.timeSlots;
    return Object.assign({}, timeSlots.find(timeSlot => timeSlot.name === timeSlotName));
  };

  setTimeSlotTwilight = timeSlotName => isTwilight => {
    const timeSlotToUpdate = this.getTimeSlotByName(timeSlotName);
    timeSlotToUpdate.isTwilight = isTwilight;
    this.props.editTimeSlot(timeSlotToUpdate);
  };

  deleteTimeSlot = timeSlotName => () => {
    this.props.deleteTimeSlot(timeSlotName);
  };

  timeChange = (timeSlotName, toOrFrom) => (e, time) => {
    const timeSlotToUpdate = this.getTimeSlotByName(timeSlotName);
    timeSlotToUpdate[toOrFrom] = moment(time).format(globals.dateFormats.localApiFormat);
    this.props.editTimeSlot(timeSlotToUpdate);
  };

  addTimeSlot = () => {
    const {
      defaultTimeSlotPackage: { timeSlots },
      addTimeSlot,
    } = this.props;
    const lastTimeslot = timeSlots[timeSlots.length - 1];
    let name = 'A';
    let from = moment(0).format(globals.dateFormats.localApiFormat);
    let diffMins = defaultDifferenceBetweenStartAndEndOfTimeslotInMinutes;

    if (lastTimeslot) {
      name = getNextName(lastTimeslot.name);
      from = lastTimeslot.to;
      diffMins = moment(lastTimeslot.to).diff(moment(lastTimeslot.from), 'minutes');
    }

    const minsInDays = 24 * 60;
    diffMins = diffMins < 0 ? minsInDays + diffMins : diffMins;

    addTimeSlot({
      name,
      from,
      to: moment(from)
        .add(diffMins, 'minutes')
        .format(globals.dateFormats.localApiFormat),
      isTwilight: false,
    });
  };

  handleSaveTimeSlotsCancel = () => {
    this.setState({
      showSaveTimeSlotsConformation: false,
    });
  };

  handleSaveTimeSlotsConfirm = () => {
    this.setState({
      showSaveTimeSlotsConformation: false,
    });
    this.props.save(this.props.defaultTimeSlotPackage.timeSlots);
  };

  render() {
    const { isFetching, defaultTimeSlotPackage, validationMessage, pageTitle } = this.props;
    let timeSlotsList = null;

    if (isFetching || !defaultTimeSlotPackage.timeSlots) {
      timeSlotsList = <Spinner />;
    } else if (defaultTimeSlotPackage.timeSlots.length < 1) {
      timeSlotsList = <div className="no-timeslots-message">{locale.getMsg('noTimeslots')}</div>;
    } else {
      const canDelete = defaultTimeSlotPackage.timeSlots.length > 1;
      timeSlotsList = defaultTimeSlotPackage.timeSlots.map(({ name, isTwilight, from, to }) => (
        <TimeSlotConfiguration
          key={name}
          name={name}
          isTwilight={isTwilight}
          from={moment(from).toDate()}
          to={moment(to).toDate()}
          toTimeChange={this.timeChange(name, 'to')}
          fromTimeChange={this.timeChange(name, 'from')}
          setTimeSlotTwilight={this.setTimeSlotTwilight(name)}
          deleteTimeSlot={this.deleteTimeSlot(name)}
          enableDelete={canDelete}
        />
      ));
    }
    return (
      <DefaultPageWrapper pageTitle={pageTitle}>
        <Box display="flex" flexDirection="column" p={1}>
          <div className="timeslots-heading">
            <Box p={1}>
              <Typography variant="h5">{locale.getMsg('newTimeSlotsEffectiveForNewTreatments')}</Typography>
            </Box>
          </div>
          {timeSlotsList}
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Box p={1}>
              <FlatButton primary label={locale.getMsg('addTimeSlot')} onClick={this.addTimeSlot} />
            </Box>
            <Box p={1}>
              <RaisedButton
                primary
                label={locale.getMsg('save')}
                onClick={() =>
                  this.setState({
                    showSaveTimeSlotsConformation: true,
                  })
                }
              />
            </Box>
          </Box>
          <div className="row">{!validationMessage ? '' : locale.getMsg(validationMessage)}</div>

          <ConfirmationDialog
            open={this.state.showSaveTimeSlotsConformation}
            handleCancel={this.handleSaveTimeSlotsCancel}
            handleConfirm={this.handleSaveTimeSlotsConfirm}
            title={locale.getMsg('saveTimeslotsConformationTitle')}
            text={locale.getMsg('saveTimeslotsConformationText')}
          />
        </Box>
      </DefaultPageWrapper>
    );
  }
}

ConfigureTimeSlots.defaultProps = {
  defaultTimeSlotPackage: null,
  validationMessage: '',
};

ConfigureTimeSlots.propTypes = {
  getDefaultTimeSlotsPackage: PropTypes.func.isRequired,
  addTimeSlot: PropTypes.func.isRequired,
  editTimeSlot: PropTypes.func.isRequired,
  deleteTimeSlot: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  defaultTimeSlotPackage: newTimeSlotsPackage,
  isFetching: PropTypes.bool.isRequired,
  validationMessage: PropTypes.string.isRequired,
  pageTitle: PropTypes.string.isRequired,
};

export default ConfigureTimeSlots;
